import React, { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';
import AddDollarAccountForm, { DollarAccountFormData } from './AddDollarAccountForm';
import {
  getDollarAccountListAPI,
  saveDollarAccountAPI,
  deleteDollarAccountAPI,
  DollarAccount,
  DollarAccountSearchRequest
} from '../../../services/adminApi';
import { ToastContainer } from '../../../components/Toast';
import { useToast } from '../../../hooks/useToast';
import { useModulePermission } from '../../../hooks/useModulePermission';
import styles from './DollarAccounts.module.css';

const DollarAccounts: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [accounts, setAccounts] = useState<DollarAccount[]>([]);
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [editingAccount, setEditingAccount] = useState<DollarAccount | null>(null);
  const permissions = useModulePermission('Dollar Account');

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const searchRequest: DollarAccountSearchRequest = {
        searchText: '',
        pageNo: 1,
        pageSize: 100,
      };
      const response = await getDollarAccountListAPI(searchRequest);
      if (response.isSuccess && response.data) {
        setAccounts(response.data);
      } else {
        showError(response.message || 'Failed to load dollar accounts');
      }
    } catch (error) {
      console.error('Failed to load dollar accounts:', error);
      showError('Failed to load dollar accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = () => {
    setEditingAccount(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAccount(null);
  };

  const handleSubmit = async (formData: DollarAccountFormData) => {
    try {
      const openingBalance = parseFloat(formData.openingBalance) || 0;
      const currentBalance = parseFloat(formData.currentBal) || 0;

      const accountPayload: DollarAccount = {
        id: editingAccount?.id,
        accountName: formData.accountName,
        accountTypeId: parseInt(formData.accountType) || 1,
        bankName: formData.bankName,
        holderName: formData.holderName,
        accountNo: formData.accountNo,
        ifsC_SwiftCode: formData.ifscSwiftCode,
        openingBal: openingBalance,
        openingDate: formData.openingDate,
        currentBal: currentBalance,
        isActive: formData.status === 'Active' || formData.status === 'active',
      };

      const response = await saveDollarAccountAPI(accountPayload);

      if (response.isSuccess) {
        showSuccess(editingAccount ? 'Dollar account updated successfully!' : 'Dollar account created successfully!');
        await loadData();
        setIsModalOpen(false);
        setEditingAccount(null);
      } else {
        showError(response.message || 'Failed to save dollar account');
      }
    } catch (error) {
      console.error('Failed to save dollar account:', error);
      showError('Failed to save dollar account. Please try again.');
    }
  };

  const handleEdit = (account: DollarAccount) => {
    setEditingAccount(account);
    setIsModalOpen(true);
  };

  const handleDelete = async (account: DollarAccount) => {
    if (window.confirm(`Are you sure you want to delete ${account.accountName}?`)) {
      try {
        const response = await deleteDollarAccountAPI(account.id || 0);
        if (response.isSuccess) {
          await loadData();
          showSuccess('Dollar account deleted successfully!');
        } else {
          showError(response.message || 'Failed to delete dollar account');
        }
      } catch (error) {
        console.error('Failed to delete dollar account:', error);
        showError('Failed to delete dollar account. Please try again.');
      }
    }
  };

  return (
    <div className={styles.container}>
      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Dollar Accounts</h1>
          <p className={styles.subtitle}>Manage your USD accounts</p>
        </div>
        {permissions.canAdd && (
          <button className={styles.addButton} onClick={handleAddAccount}>
            + Add Account
          </button>
        )}
      </div>

      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>Loading dollar accounts...</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>NAME</th>
                <th>TYPE</th>
                <th>BANK/DETAILS</th>
                <th>OPENING BAL</th>
                <th>CURRENT BAL</th>
                <th>STATUS</th>
                {(permissions.canEdit || permissions.canVoid) && <th>ACTIONS</th>}
              </tr>
            </thead>
            <tbody>
              {accounts.length === 0 ? (
                <tr>
                  <td colSpan={permissions.canEdit || permissions.canVoid ? 7 : 6} className={styles.emptyState}>No dollar accounts found</td>
                </tr>
              ) : (
                accounts.map((account) => (
                  <tr key={account.id}>
                    <td className={styles.nameCell}>{account.accountName}</td>
                    <td>
                      <span className={`${styles.typeBadge} ${styles[account.accountType?.toLowerCase().replace(' ', '') || '']}`}>
                        {account.accountType || account.accountTypeId}
                      </span>
                    </td>
                    <td>
                      {account.bankName && <div><strong>{account.bankName}</strong></div>}
                      {account.accountNo && <div>{account.accountNo}</div>}
                      {account.holderName && <div className={styles.holderName}>{account.holderName}</div>}
                    </td>
                    <td>${(account.openingBal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>${(account.currentBal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[account.isActive ? 'active' : 'inactive']}`}>
                        {account.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {(permissions.canEdit || permissions.canVoid) && (
                      <td>
                        <div className={styles.actions}>
                          {permissions.canEdit && (
                            <button
                              className={styles.actionIcon}
                              onClick={() => handleEdit(account)}
                              title="Edit"
                            >
                              ✏️
                            </button>
                          )}
                          {permissions.canVoid && (
                            <button
                              className={`${styles.actionIcon} ${styles.deleteIcon}`}
                              onClick={() => handleDelete(account)}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingAccount ? 'Edit Dollar Account' : 'Add New Dollar Account'}
      >
        <AddDollarAccountForm
          onSubmit={handleSubmit}
          onCancel={handleCloseModal}
          initialData={editingAccount ? {
            accountName: editingAccount.accountName,
            accountType: editingAccount.accountTypeId.toString(),
            bankName: editingAccount.bankName || '',
            holderName: editingAccount.holderName || '',
            accountNo: editingAccount.accountNo || '',
            ifscSwiftCode: editingAccount.ifsC_SwiftCode || '',
            openingBalance: editingAccount.openingBal.toString(),
            openingDate: editingAccount.openingDate ? editingAccount.openingDate.split('T')[0] : new Date().toISOString().split('T')[0],
            status: editingAccount.isActive ? 'Active' : 'Inactive',
          } : undefined}
        />
      </Modal>
    </div>
  );
};

export default DollarAccounts;
